/**
 * Pure detail view-model for the Scan Results Dashboard (Req 8, 9, 10.2).
 *
 * `buildDetailViewModel` maps one original `RepoScanResult` (the byte-identical
 * `<repo>.json` the scanner wrote) into a typed, render-ready model that the
 * `RepoDetail` component displays section by section. It is the SPA-side target
 * of design Property 13 (detail completeness) and Property 14 (secret-safety /
 * closed schema).
 *
 * Design constraints encoded here:
 *
 *  - **Total / never raises.** Every field is read defensively. A `null`,
 *    missing, empty, or wrong-typed value yields a placeholder rather than an
 *    exception, so the detail view always renders (Req 8.3, 10.2, 10.4).
 *  - **Every section and field exposed.** The seven top-level fields, all
 *    metadata fields (Req 8.2), and every documentation / ownership / cicd /
 *    manifests / infra / api (including each route's `source_path`, `method`,
 *    `path`, `framework`, `snippet`) / runtime / domain / errors /
 *    truncated_files field is present in the model (Req 8.1, 8.4-8.10).
 *  - **Placeholders for null, "no entries" for empty collections** (Req 8.3,
 *    8.11, 9.4).
 *  - **Secret-safety.** The runtime section emits `env_var_names` and
 *    `secret_like_env_vars` as *name strings only*; every `secret_like_env_vars`
 *    entry is marked secret-like (Req 9.1, 9.2). No value is ever read or
 *    emitted because no value exists in the schema.
 *  - **Closed schema.** The model has a fixed, typed shape built only from
 *    defined `RepoScanResult` fields, so no out-of-schema value can be emitted.
 *    Any extra (non-schema) keys present on the source object are omitted and
 *    surfaced by name only through a "withheld" indicator (Req 9.3, 9.5).
 *
 * The function is pure: it never mutates its input and depends only on its
 * argument, so it is deterministic and exhaustively property-testable.
 */

import type { RepoScanResult } from "../types";

// ---------------------------------------------------------------------------
// Indicator strings (the visible textual indicators the UI renders verbatim)
// ---------------------------------------------------------------------------

/** Placeholder shown for a `null`, absent, or empty scalar field (Req 8.3, 10.4). */
export const NOT_PROVIDED = "Not provided";

/** Placeholder shown for an empty list-valued field (Req 8.11, 9.4). */
export const NO_ENTRIES = "No entries";

/** Placeholder shown when the whole GitHub metadata object is absent (Req 10.2). */
export const METADATA_UNAVAILABLE = "GitHub metadata unavailable";

/** Indicator marking a `secret_like_env_vars` entry as secret-like (Req 9.2). */
export const SECRET_LIKE_MARKER = "secret-like";

/** Indicator shown when one or more out-of-schema values were withheld (Req 9.5). */
export const WITHHELD = "Value withheld (not part of the scan schema)";

// ---------------------------------------------------------------------------
// Field / collection representations
// ---------------------------------------------------------------------------

/**
 * A single scalar field that is either present with a value or rendered as a
 * placeholder (used wherever a value may be `null`/absent/empty — Req 8.3).
 */
export type Field<T> =
  | { present: true; value: T }
  | { present: false; placeholder: string };

/**
 * A list-valued field that is either non-empty with mapped entries or rendered
 * as a "no entries" indicator when empty/absent (Req 8.11, 9.4).
 */
export type Collection<T> =
  | { empty: false; entries: T[] }
  | { empty: true; placeholder: string };

/** One `languages` map entry: language name and its byte count. */
export interface LanguageEntry {
  name: string;
  bytes: Field<number>;
}

/**
 * One environment-variable entry in the runtime section. Carries the variable
 * *name only*; `secretLike` flags entries sourced from `secret_like_env_vars`
 * and `marker` holds the visible secret-like indicator for those (Req 9.1, 9.2).
 */
export interface EnvVarEntry {
  name: string;
  secretLike: boolean;
  marker: string | null;
}

/** One detected HTTP route (Req 8.7). Every field may be absent. */
export interface RouteEntry {
  sourcePath: Field<string>;
  method: Field<string>;
  path: Field<string>;
  framework: Field<string>;
  snippet: Field<string>;
}

/** One domain keyword match (Req 8.9). */
export interface DomainMatchEntry {
  group: Field<string>;
  keyword: Field<string>;
  path: Field<string>;
}

/** One recorded scan error (Req 8.10). */
export interface ErrorEntry {
  stage: Field<string>;
  code: Field<string>;
  message: Field<string>;
  path: Field<string>;
}

// ---------------------------------------------------------------------------
// Section shapes
// ---------------------------------------------------------------------------

/** The seven always-present top-level fields (Req 8.1). */
export interface TopLevelSection {
  scannerVersion: Field<string>;
  scanId: Field<string>;
  scanStartedAt: Field<string>;
  scanCompletedAt: Field<string>;
  repoUrl: Field<string>;
  repoName: Field<string>;
  status: Field<string>;
}

/** All GitHub metadata fields displayed when metadata is present (Req 8.2). */
export interface MetadataFields {
  name: Field<string>;
  fullName: Field<string>;
  htmlUrl: Field<string>;
  cloneUrl: Field<string>;
  defaultBranch: Field<string>;
  archived: Field<boolean>;
  visibility: Field<string>;
  private: Field<boolean>;
  createdAt: Field<string>;
  updatedAt: Field<string>;
  pushedAt: Field<string>;
  lastCommitDate: Field<string>;
  primaryLanguage: Field<string>;
  languages: Collection<LanguageEntry>;
  topics: Collection<string>;
  description: Field<string>;
  size: Field<number>;
}

/**
 * The metadata section: either the full set of fields (metadata present) or a
 * single placeholder indicating GitHub metadata is unavailable (Req 8.2, 10.2).
 */
export type MetadataSection =
  | { available: true; fields: MetadataFields }
  | { available: false; placeholder: string };

/** The `documentation` section (Req 8.4). */
export interface DocumentationSection {
  readmeFound: Field<boolean>;
  readmePath: Field<string>;
  readmeExcerpt: Field<string>;
  readmeQualityGuess: Field<string>;
  docsDirFound: Field<boolean>;
  runbookFound: Field<boolean>;
  adrFound: Field<boolean>;
  openapiFound: Field<boolean>;
  swaggerFound: Field<boolean>;
  asyncapiFound: Field<boolean>;
  docsPaths: Collection<string>;
  runbookPaths: Collection<string>;
  adrPaths: Collection<string>;
  openapiPaths: Collection<string>;
  swaggerPaths: Collection<string>;
  asyncapiPaths: Collection<string>;
}

/** The `ownership` section (Req 8.5). */
export interface OwnershipSection {
  owningTeamGuess: Field<string>;
  ownerConfidence: Field<string>;
  codeownersPaths: Collection<string>;
  catalogInfoPaths: Collection<string>;
  readmeOwnerHints: Collection<string>;
  evidencePaths: Collection<string>;
}

/** The `cicd` section (Req 8.6). */
export interface CicdSection {
  githubActionsFound: Field<boolean>;
  deployWorkflowGuess: Field<boolean>;
  workflowPaths: Collection<string>;
  buildspecPaths: Collection<string>;
  jenkinsfilePaths: Collection<string>;
  evidencePaths: Collection<string>;
}

/** The `manifests` section (Req 8.6). */
export interface ManifestsSection {
  packageJsonFound: Field<boolean>;
  pomXmlFound: Field<boolean>;
  buildGradleFound: Field<boolean>;
  requirementsTxtFound: Field<boolean>;
  pyprojectTomlFound: Field<boolean>;
  goModFound: Field<boolean>;
  cargoTomlFound: Field<boolean>;
  gemfileFound: Field<boolean>;
  composerJsonFound: Field<boolean>;
  csprojFound: Field<boolean>;
  solutionFound: Field<boolean>;
  manifestPaths: Collection<string>;
  dependenciesDetected: Collection<string>;
  detectedPackageManagers: Collection<string>;
  parseIncomplete: Field<boolean>;
  parseWarnings: Collection<string>;
}

/** The `infra` section (Req 8.6). */
export interface InfraSection {
  dockerfileFound: Field<boolean>;
  dockerComposeFound: Field<boolean>;
  kubernetesFound: Field<boolean>;
  helmFound: Field<boolean>;
  terraformFound: Field<boolean>;
  serverlessFound: Field<boolean>;
  cloudformationFound: Field<boolean>;
  samFound: Field<boolean>;
  dockerfilePaths: Collection<string>;
  dockerComposePaths: Collection<string>;
  kubernetesPaths: Collection<string>;
  helmPaths: Collection<string>;
  terraformPaths: Collection<string>;
  serverlessPaths: Collection<string>;
  cloudformationPaths: Collection<string>;
  samPaths: Collection<string>;
  parseWarnings: Collection<string>;
}

/** The `api` section including each detected route (Req 8.7). */
export interface ApiSection {
  openapiPaths: Collection<string>;
  swaggerPaths: Collection<string>;
  asyncapiPaths: Collection<string>;
  protoPaths: Collection<string>;
  routes: Collection<RouteEntry>;
  parseWarnings: Collection<string>;
}

/** The `runtime` section — names only, secret-like entries marked (Req 8.8, 9). */
export interface RuntimeSection {
  dockerBaseImages: Collection<string>;
  exposedPorts: Collection<number>;
  envVarNames: Collection<EnvVarEntry>;
  secretLikeEnvVars: Collection<EnvVarEntry>;
  cronOrScheduleDetected: Field<boolean>;
  parseWarnings: Collection<string>;
}

/** The `domain` section (Req 8.9). */
export interface DomainSection {
  matches: Collection<DomainMatchEntry>;
}

/**
 * The closed-schema / withheld indicator (Req 9.5). When the source object
 * carries keys outside the defined schema, they are omitted from display and
 * reported here by name only (never their values).
 */
export type WithheldInfo =
  | { hasWithheld: false }
  | { hasWithheld: true; indicator: string; fieldNames: string[] };

/** The complete render-ready detail view-model for one `RepoScanResult`. */
export interface DetailViewModel {
  topLevel: TopLevelSection;
  metadata: MetadataSection;
  documentation: DocumentationSection;
  ownership: OwnershipSection;
  cicd: CicdSection;
  manifests: ManifestsSection;
  infra: InfraSection;
  api: ApiSection;
  runtime: RuntimeSection;
  domain: DomainSection;
  errors: Collection<ErrorEntry>;
  truncatedFiles: Collection<string>;
  withheld: WithheldInfo;
}

// ---------------------------------------------------------------------------
// Defensive scalar/collection helpers (total — never raise)
// ---------------------------------------------------------------------------

/** A loose record view of an unknown value, for safe defensive key access. */
type Loose = Record<string, unknown>;

/** Read a sub-object as a loose record; non-objects become `{}` (total). */
function obj(value: unknown): Loose {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Loose)
    : {};
}

/** Present a boolean field; non-booleans become a placeholder. */
function boolField(value: unknown, placeholder = NOT_PROVIDED): Field<boolean> {
  return typeof value === "boolean"
    ? { present: true, value }
    : { present: false, placeholder };
}

/** Present a string field; `null`/absent/empty strings become a placeholder (Req 8.3). */
function stringField(value: unknown, placeholder = NOT_PROVIDED): Field<string> {
  return typeof value === "string" && value.length > 0
    ? { present: true, value }
    : { present: false, placeholder };
}

/** Present a finite-number field; anything else becomes a placeholder. */
function numberField(value: unknown, placeholder = NOT_PROVIDED): Field<number> {
  return typeof value === "number" && Number.isFinite(value)
    ? { present: true, value }
    : { present: false, placeholder };
}

/**
 * Build a {@link Collection} from a possibly-absent array. An empty or
 * non-array input yields the "no entries" indicator (Req 8.11, 9.4); otherwise
 * each element is mapped. Never mutates the input.
 */
function collection<TOut>(
  value: unknown,
  map: (element: unknown, index: number) => TOut,
  placeholder = NO_ENTRIES,
): Collection<TOut> {
  if (Array.isArray(value) && value.length > 0) {
    return { empty: false, entries: value.map(map) };
  }
  return { empty: true, placeholder };
}

/** A string {@link Collection}: each element coerced to its string form. */
function stringCollection(value: unknown): Collection<string> {
  return collection(value, (element) => String(element));
}

// ---------------------------------------------------------------------------
// Closed-schema (withheld) detection — Req 9.5
// ---------------------------------------------------------------------------

/** Defined keys of every schema object, used to detect out-of-schema extras. */
const SCHEMA_KEYS = {
  root: [
    "scanner_version",
    "scan_id",
    "scan_started_at",
    "scan_completed_at",
    "repo_url",
    "repo_name",
    "status",
    "metadata",
    "documentation",
    "ownership",
    "cicd",
    "manifests",
    "infra",
    "api",
    "runtime",
    "domain",
    "errors",
    "truncated_files",
  ],
  metadata: [
    "name",
    "full_name",
    "html_url",
    "clone_url",
    "default_branch",
    "archived",
    "visibility",
    "private",
    "created_at",
    "updated_at",
    "pushed_at",
    "last_commit_date",
    "primary_language",
    "languages",
    "topics",
    "description",
    "size",
  ],
  documentation: [
    "readme_found",
    "readme_path",
    "readme_excerpt",
    "readme_quality_guess",
    "docs_dir_found",
    "runbook_found",
    "adr_found",
    "openapi_found",
    "swagger_found",
    "asyncapi_found",
    "docs_paths",
    "runbook_paths",
    "adr_paths",
    "openapi_paths",
    "swagger_paths",
    "asyncapi_paths",
  ],
  ownership: [
    "codeowners_paths",
    "catalog_info_paths",
    "readme_owner_hints",
    "owning_team_guess",
    "owner_confidence",
    "evidence_paths",
  ],
  cicd: [
    "github_actions_found",
    "deploy_workflow_guess",
    "workflow_paths",
    "buildspec_paths",
    "jenkinsfile_paths",
    "evidence_paths",
  ],
  manifests: [
    "package_json_found",
    "pom_xml_found",
    "build_gradle_found",
    "requirements_txt_found",
    "pyproject_toml_found",
    "go_mod_found",
    "cargo_toml_found",
    "gemfile_found",
    "composer_json_found",
    "csproj_found",
    "solution_found",
    "manifest_paths",
    "dependencies_detected",
    "detected_package_managers",
    "parse_incomplete",
    "parse_warnings",
  ],
  infra: [
    "dockerfile_found",
    "docker_compose_found",
    "kubernetes_found",
    "helm_found",
    "terraform_found",
    "serverless_found",
    "cloudformation_found",
    "sam_found",
    "dockerfile_paths",
    "docker_compose_paths",
    "kubernetes_paths",
    "helm_paths",
    "terraform_paths",
    "serverless_paths",
    "cloudformation_paths",
    "sam_paths",
    "parse_warnings",
  ],
  api: [
    "openapi_paths",
    "swagger_paths",
    "asyncapi_paths",
    "proto_paths",
    "routes",
    "parse_warnings",
  ],
  runtime: [
    "docker_base_images",
    "exposed_ports",
    "env_var_names",
    "secret_like_env_vars",
    "cron_or_schedule_detected",
    "parse_warnings",
  ],
  domain: ["matches"],
} as const;

/**
 * Collect the names of keys present on `source` but absent from `known`,
 * prefixed by the section path (e.g. `runtime.surprise`). Names only — values
 * are never read or emitted (Req 9.5). Non-objects contribute nothing.
 */
function withheldKeysOf(
  source: unknown,
  known: readonly string[],
  prefix: string,
): string[] {
  if (typeof source !== "object" || source === null || Array.isArray(source)) {
    return [];
  }
  const knownSet = new Set(known);
  const extras: string[] = [];
  for (const key of Object.keys(source as Loose)) {
    if (!knownSet.has(key)) {
      extras.push(prefix === "" ? key : `${prefix}.${key}`);
    }
  }
  return extras;
}

/**
 * Detect every out-of-schema key across the source object and its known
 * sub-objects, reporting them by name through a {@link WithheldInfo} (Req 9.5).
 */
function buildWithheld(r: Loose): WithheldInfo {
  const fieldNames = [
    ...withheldKeysOf(r, SCHEMA_KEYS.root, ""),
    ...withheldKeysOf(r.metadata, SCHEMA_KEYS.metadata, "metadata"),
    ...withheldKeysOf(r.documentation, SCHEMA_KEYS.documentation, "documentation"),
    ...withheldKeysOf(r.ownership, SCHEMA_KEYS.ownership, "ownership"),
    ...withheldKeysOf(r.cicd, SCHEMA_KEYS.cicd, "cicd"),
    ...withheldKeysOf(r.manifests, SCHEMA_KEYS.manifests, "manifests"),
    ...withheldKeysOf(r.infra, SCHEMA_KEYS.infra, "infra"),
    ...withheldKeysOf(r.api, SCHEMA_KEYS.api, "api"),
    ...withheldKeysOf(r.runtime, SCHEMA_KEYS.runtime, "runtime"),
    ...withheldKeysOf(r.domain, SCHEMA_KEYS.domain, "domain"),
  ];

  if (fieldNames.length === 0) {
    return { hasWithheld: false };
  }
  return { hasWithheld: true, indicator: WITHHELD, fieldNames };
}

// ---------------------------------------------------------------------------
// Section builders
// ---------------------------------------------------------------------------

function buildTopLevel(r: Loose): TopLevelSection {
  return {
    scannerVersion: stringField(r.scanner_version),
    scanId: stringField(r.scan_id),
    scanStartedAt: stringField(r.scan_started_at),
    scanCompletedAt: stringField(r.scan_completed_at),
    repoUrl: stringField(r.repo_url),
    repoName: stringField(r.repo_name),
    status: stringField(r.status),
  };
}

function buildMetadata(value: unknown): MetadataSection {
  // Null/absent metadata: a single unavailable placeholder (Req 10.2).
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return { available: false, placeholder: METADATA_UNAVAILABLE };
  }
  const m = value as Loose;
  const languages = obj(m.languages);
  const languageEntries: LanguageEntry[] = Object.keys(languages).map((name) => ({
    name,
    bytes: numberField(languages[name]),
  }));

  const fields: MetadataFields = {
    name: stringField(m.name),
    fullName: stringField(m.full_name),
    htmlUrl: stringField(m.html_url),
    cloneUrl: stringField(m.clone_url),
    defaultBranch: stringField(m.default_branch),
    archived: boolField(m.archived),
    visibility: stringField(m.visibility),
    private: boolField(m.private),
    createdAt: stringField(m.created_at),
    updatedAt: stringField(m.updated_at),
    pushedAt: stringField(m.pushed_at),
    lastCommitDate: stringField(m.last_commit_date),
    primaryLanguage: stringField(m.primary_language),
    languages:
      languageEntries.length > 0
        ? { empty: false, entries: languageEntries }
        : { empty: true, placeholder: NO_ENTRIES },
    topics: stringCollection(m.topics),
    description: stringField(m.description),
    size: numberField(m.size),
  };
  return { available: true, fields };
}

function buildDocumentation(value: unknown): DocumentationSection {
  const d = obj(value);
  return {
    readmeFound: boolField(d.readme_found),
    readmePath: stringField(d.readme_path),
    readmeExcerpt: stringField(d.readme_excerpt),
    readmeQualityGuess: stringField(d.readme_quality_guess),
    docsDirFound: boolField(d.docs_dir_found),
    runbookFound: boolField(d.runbook_found),
    adrFound: boolField(d.adr_found),
    openapiFound: boolField(d.openapi_found),
    swaggerFound: boolField(d.swagger_found),
    asyncapiFound: boolField(d.asyncapi_found),
    docsPaths: stringCollection(d.docs_paths),
    runbookPaths: stringCollection(d.runbook_paths),
    adrPaths: stringCollection(d.adr_paths),
    openapiPaths: stringCollection(d.openapi_paths),
    swaggerPaths: stringCollection(d.swagger_paths),
    asyncapiPaths: stringCollection(d.asyncapi_paths),
  };
}

function buildOwnership(value: unknown): OwnershipSection {
  const o = obj(value);
  return {
    owningTeamGuess: stringField(o.owning_team_guess),
    ownerConfidence: stringField(o.owner_confidence),
    codeownersPaths: stringCollection(o.codeowners_paths),
    catalogInfoPaths: stringCollection(o.catalog_info_paths),
    readmeOwnerHints: stringCollection(o.readme_owner_hints),
    evidencePaths: stringCollection(o.evidence_paths),
  };
}

function buildCicd(value: unknown): CicdSection {
  const c = obj(value);
  return {
    githubActionsFound: boolField(c.github_actions_found),
    deployWorkflowGuess: boolField(c.deploy_workflow_guess),
    workflowPaths: stringCollection(c.workflow_paths),
    buildspecPaths: stringCollection(c.buildspec_paths),
    jenkinsfilePaths: stringCollection(c.jenkinsfile_paths),
    evidencePaths: stringCollection(c.evidence_paths),
  };
}

function buildManifests(value: unknown): ManifestsSection {
  const m = obj(value);
  return {
    packageJsonFound: boolField(m.package_json_found),
    pomXmlFound: boolField(m.pom_xml_found),
    buildGradleFound: boolField(m.build_gradle_found),
    requirementsTxtFound: boolField(m.requirements_txt_found),
    pyprojectTomlFound: boolField(m.pyproject_toml_found),
    goModFound: boolField(m.go_mod_found),
    cargoTomlFound: boolField(m.cargo_toml_found),
    gemfileFound: boolField(m.gemfile_found),
    composerJsonFound: boolField(m.composer_json_found),
    csprojFound: boolField(m.csproj_found),
    solutionFound: boolField(m.solution_found),
    manifestPaths: stringCollection(m.manifest_paths),
    dependenciesDetected: stringCollection(m.dependencies_detected),
    detectedPackageManagers: stringCollection(m.detected_package_managers),
    parseIncomplete: boolField(m.parse_incomplete),
    parseWarnings: stringCollection(m.parse_warnings),
  };
}

function buildInfra(value: unknown): InfraSection {
  const i = obj(value);
  return {
    dockerfileFound: boolField(i.dockerfile_found),
    dockerComposeFound: boolField(i.docker_compose_found),
    kubernetesFound: boolField(i.kubernetes_found),
    helmFound: boolField(i.helm_found),
    terraformFound: boolField(i.terraform_found),
    serverlessFound: boolField(i.serverless_found),
    cloudformationFound: boolField(i.cloudformation_found),
    samFound: boolField(i.sam_found),
    dockerfilePaths: stringCollection(i.dockerfile_paths),
    dockerComposePaths: stringCollection(i.docker_compose_paths),
    kubernetesPaths: stringCollection(i.kubernetes_paths),
    helmPaths: stringCollection(i.helm_paths),
    terraformPaths: stringCollection(i.terraform_paths),
    serverlessPaths: stringCollection(i.serverless_paths),
    cloudformationPaths: stringCollection(i.cloudformation_paths),
    samPaths: stringCollection(i.sam_paths),
    parseWarnings: stringCollection(i.parse_warnings),
  };
}

function buildApi(value: unknown): ApiSection {
  const a = obj(value);
  return {
    openapiPaths: stringCollection(a.openapi_paths),
    swaggerPaths: stringCollection(a.swagger_paths),
    asyncapiPaths: stringCollection(a.asyncapi_paths),
    protoPaths: stringCollection(a.proto_paths),
    routes: collection(a.routes, (route): RouteEntry => {
      const rt = obj(route);
      return {
        sourcePath: stringField(rt.source_path),
        method: stringField(rt.method),
        path: stringField(rt.path),
        framework: stringField(rt.framework),
        snippet: stringField(rt.snippet),
      };
    }),
    parseWarnings: stringCollection(a.parse_warnings),
  };
}

/**
 * Build one environment-variable {@link Collection}, emitting *names only*. When
 * `secretLike` is true every entry is flagged and given the secret-like marker
 * (Req 9.1, 9.2); empty/absent collections yield the "no entries" indicator
 * (Req 9.4).
 */
function buildEnvVars(
  value: unknown,
  secretLike: boolean,
): Collection<EnvVarEntry> {
  return collection(value, (name): EnvVarEntry => ({
    name: String(name),
    secretLike,
    marker: secretLike ? SECRET_LIKE_MARKER : null,
  }));
}

function buildRuntime(value: unknown): RuntimeSection {
  const rt = obj(value);
  return {
    dockerBaseImages: stringCollection(rt.docker_base_images),
    exposedPorts: collection(rt.exposed_ports, (port) => Number(port)),
    // Names only — no value is read because none exists in the schema (Req 9.1).
    envVarNames: buildEnvVars(rt.env_var_names, false),
    secretLikeEnvVars: buildEnvVars(rt.secret_like_env_vars, true),
    cronOrScheduleDetected: boolField(rt.cron_or_schedule_detected),
    parseWarnings: stringCollection(rt.parse_warnings),
  };
}

function buildDomain(value: unknown): DomainSection {
  const d = obj(value);
  return {
    matches: collection(d.matches, (match): DomainMatchEntry => {
      const mt = obj(match);
      return {
        group: stringField(mt.group),
        keyword: stringField(mt.keyword),
        path: stringField(mt.path),
      };
    }),
  };
}

function buildErrors(value: unknown): Collection<ErrorEntry> {
  return collection(value, (error): ErrorEntry => {
    const e = obj(error);
    return {
      stage: stringField(e.stage),
      code: stringField(e.code),
      message: stringField(e.message),
      path: stringField(e.path),
    };
  });
}

// ---------------------------------------------------------------------------
// Public entry point
// ---------------------------------------------------------------------------

/**
 * Map a `RepoScanResult` to its render-ready {@link DetailViewModel} (Req 8, 9,
 * 10.2; design Property 13 and Property 14).
 *
 * The function is pure and total: it never mutates `result`, depends only on
 * its argument, and returns a fully-populated model for any input — including a
 * `null`/malformed object — without raising. Null/empty scalars become
 * placeholders, empty collections become "no entries" indicators, the runtime
 * section emits environment-variable *names only* (secret-like entries marked),
 * and any out-of-schema keys are omitted and surfaced by name as withheld.
 *
 * The parameter is typed `RepoScanResult` for callers, but every access is
 * defensive so a runtime value that drifts from the type still renders safely.
 */
export function buildDetailViewModel(result: RepoScanResult): DetailViewModel {
  const r = obj(result);
  return {
    topLevel: buildTopLevel(r),
    metadata: buildMetadata(r.metadata),
    documentation: buildDocumentation(r.documentation),
    ownership: buildOwnership(r.ownership),
    cicd: buildCicd(r.cicd),
    manifests: buildManifests(r.manifests),
    infra: buildInfra(r.infra),
    api: buildApi(r.api),
    runtime: buildRuntime(r.runtime),
    domain: buildDomain(r.domain),
    errors: buildErrors(r.errors),
    truncatedFiles: stringCollection(r.truncated_files),
    withheld: buildWithheld(r),
  };
}
